import Area from '@/components/Area';
import { CameraControls, shaderMaterial } from '@react-three/drei';
import { Canvas, extend, GroupProps, ReactThreeFiber } from '@react-three/fiber';
import * as React from 'react';
import * as THREE from 'three';
import { schemeCategory10 } from 'd3-scale-chromatic';

type MeshEdgesMaterialProps = {
  color?: THREE.ColorRepresentation;
  size?: THREE.Vector3 | THREE.Vector3Tuple;
  thickness?: number;
  smoothness?: number;
};

const MeshEdgesMaterial = shaderMaterial(
  {
    color: new THREE.Color('white'),
    size: new THREE.Vector3(1, 1, 1),
    thickness: 0.01,
    smoothness: 0.2,
  },
  /*glsl*/ `varying vec3 vPosition;
  void main() {
    vPosition = position;
    gl_Position = projectionMatrix * viewMatrix * instanceMatrix * vec4(position, 1.0);
  }`,
  /*glsl*/ `varying vec3 vPosition;
  uniform vec3 size;
  uniform vec3 color;
  uniform float thickness;
  uniform float smoothness;
  void main() {
    vec3 d = abs(vPosition) - (size * 0.5);
    float a = smoothstep(thickness, thickness + smoothness, min(min(length(d.xy), length(d.yz)), length(d.xz)));
    gl_FragColor = vec4(color, 1.0 - a);
  }`
);

extend({ MeshEdgesMaterial });

declare global {
  namespace JSX {
    interface IntrinsicElements {
      meshEdgesMaterial: ReactThreeFiber.Node<
        typeof MeshEdgesMaterial & JSX.IntrinsicElements['shaderMaterial'],
        MeshEdgesMaterialProps
      > &
        MeshEdgesMaterialProps;
    }
  }
}

type BoxesProps = {
  edgeCount?: number;
  size?: [number, number, number];
} & GroupProps;

const c = new THREE.Color();
const o = new THREE.Object3D();

function Boxes({ edgeCount = 10, size = [0.15, 0.15, 0.15], ...props }: BoxesProps) {
  const count = edgeCount ** 3;
  const ref = React.useRef<THREE.InstancedMesh>(null!);
  const outlines = React.useRef<THREE.InstancedMesh>(null!);
  const colors = React.useMemo(
    () =>
      new Float32Array(
        Array.from({ length: count }, () => c.set(schemeCategory10[Math.floor(Math.random() * 10)]).toArray()).flat()
      ),
    [count]
  );
  React.useLayoutEffect(() => {
    let i = 0;
    // const root = Math.round(Math.cbrt(count));
    const root = edgeCount;
    const halfRoot = root / 2;
    console.log('root', root, 'halfRoot', halfRoot);
    for (let x = 0; x < root; x++) {
      for (let y = 0; y < root; y++) {
        for (let z = 0; z < root; z++) {
          const id = i++;
          o.rotation.set(Math.random() * Math.PI, Math.random() * Math.PI, Math.random() * Math.PI);
          o.position.set(halfRoot - x + Math.random(), halfRoot - y + Math.random(), halfRoot - z + Math.random());
          o.updateMatrix();
          ref.current.setMatrixAt(id, o.matrix);
          // o.position.set(halfRoot - x, halfRoot - y, halfRoot - z);
          // o.position.set(x, y, z);
          // console.log('id', id, 'x', x, 'y', y, 'z', z, 'position', o.position);
        }
      }
    }
    ref.current.instanceMatrix.needsUpdate = true;
    outlines.current.geometry = ref.current.geometry;
    outlines.current.instanceMatrix = ref.current.instanceMatrix;
  }, [edgeCount]);

  return (
    <group {...props}>
      <instancedMesh ref={ref} args={[undefined, undefined, count]}>
        <boxGeometry args={size}>
          {/* <instancedBufferAttribute attach="instancePosition" args={[new Float32Array(length * 3), 3]} /> */}
          <instancedBufferAttribute attach="attributes-color" args={[colors, 3]} />
        </boxGeometry>
        <meshLambertMaterial vertexColors toneMapped={false} />
        {/* <meshStandardMaterial toneMapped={false} /> */}
      </instancedMesh>
      <instancedMesh ref={outlines} args={[undefined, undefined, count]}>
        <meshEdgesMaterial
          transparent
          polygonOffset
          polygonOffsetFactor={-1}
          size={size}
          color="black"
          thickness={0.001}
          smoothness={0.05}
        />
      </instancedMesh>
    </group>
  );
}

export type Props = {};

function App01(props: Props) {
  const {} = props;

  return (
    <Area>
      {() => (
        <Canvas>
          <ambientLight intensity={0.85} />
          <directionalLight position={[150, 150, 150]} intensity={1} />
          <Boxes />
          {/* <mesh>
            <boxGeometry args={[1, 1, 1]} />
            <meshStandardMaterial color="red" />
          </mesh> */}
          <gridHelper args={[10, 10]} />
          <axesHelper args={[5]} />
          <CameraControls />
          {/* <OrbitControls /> */}
        </Canvas>
      )}
    </Area>
  );
}

export default React.memo(App01);
